import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import { withdrawals, type Withdrawal } from "@/api/withdrawals";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
} from "@/components/ui";

/**
 * Admin withdrawal review queue.
 *
 *   * Pending list at the top — approve or reject each request.
 *   * Recent history below for audit context.
 *   * Per-row "mark paid" once approved (terminal status when wire/ach lands).
 */
export function AdminWithdrawalsPage() {
  const pending = useQuery({
    queryKey: ["withdrawals", "admin", "pending"],
    queryFn: () => withdrawals.adminPending(),
    refetchInterval: 15_000,
  });
  const all = useQuery({
    queryKey: ["withdrawals", "admin", "all"],
    queryFn: () => withdrawals.adminAll(100),
    refetchInterval: 30_000,
  });
  const [decision, setDecision] = useState<{ row: Withdrawal; kind: "approve" | "reject" | "mark-paid" } | null>(null);

  return (
    <div className="space-y-3">
      <h1 className="text-base font-semibold text-term-fg">Withdrawals</h1>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Pending approval</span>
            <Badge tone={(pending.data?.length ?? 0) > 0 ? "warn" : "neutral"}>
              {pending.data?.length ?? 0}
            </Badge>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {pending.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : !pending.data || pending.data.length === 0 ? (
            <EmptyState
              title="Queue empty"
              description="No client withdrawal requests awaiting review."
            />
          ) : (
            <WithdrawalTable rows={pending.data} pending onAction={setDecision} />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium">Recent</span>
        </CardHeader>
        <CardBody className="p-0">
          {all.data && all.data.length > 0 ? (
            <WithdrawalTable rows={all.data} pending={false} onAction={setDecision} />
          ) : (
            <EmptyState title="No history" description="Approved + rejected requests appear here." />
          )}
        </CardBody>
      </Card>

      {decision && (
        <DecisionModal
          row={decision.row}
          kind={decision.kind}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  );
}


function WithdrawalTable({
  rows,
  pending,
  onAction,
}: {
  rows: Withdrawal[];
  pending: boolean;
  onAction: (d: { row: Withdrawal; kind: "approve" | "reject" | "mark-paid" }) => void;
}) {
  return (
    <table className="w-full text-xs">
      <thead className="bg-term-elev text-term-mute">
        <tr>
          <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Created</th>
          <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Amount</th>
          <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
          <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Account</th>
          <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Note</th>
          <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((w) => (
          <tr key={w.id} className="border-t border-term-border hover:bg-term-elev">
            <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
              {new Date(w.created_at).toLocaleString()}
            </td>
            <td className="px-3 py-1.5 text-right tnum text-term-fg">
              {w.amount} {w.currency}
            </td>
            <td className="px-3 py-1.5">{statusBadge(w.status)}</td>
            <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">
              {w.trading_account_id.slice(0, 8)}
            </td>
            <td className="px-3 py-1.5 text-2xs text-term-dim">
              {w.note ?? "—"}
              {w.decision_note && (
                <div className="italic text-term-mute">› {w.decision_note}</div>
              )}
            </td>
            <td className="px-3 py-1.5 text-right">
              <div className="flex justify-end gap-1">
                {pending && w.status === "PENDING" && (
                  <>
                    <Button onClick={() => onAction({ row: w, kind: "approve" })}>Approve</Button>
                    <Button variant="danger" onClick={() => onAction({ row: w, kind: "reject" })}>
                      Reject
                    </Button>
                  </>
                )}
                {w.status === "APPROVED" && (
                  <Button variant="ghost" onClick={() => onAction({ row: w, kind: "mark-paid" })}>
                    Mark paid
                  </Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


function statusBadge(s: string) {
  const tone =
    s === "APPROVED" || s === "PAID" ? "good"
      : s === "PENDING" ? "warn"
        : s === "REJECTED" || s === "CANCELED" ? "bad"
          : "neutral";
  return <Badge tone={tone as "good" | "bad" | "warn" | "neutral"}>{s}</Badge>;
}


function DecisionModal({
  row,
  kind,
  onClose,
}: {
  row: Withdrawal;
  kind: "approve" | "reject" | "mark-paid";
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: async () => {
      if (kind === "approve") return withdrawals.approve(row.id, note || undefined);
      if (kind === "reject") return withdrawals.reject(row.id, note || undefined);
      return withdrawals.markPaid(row.id, note || undefined);
    },
    onSuccess: () => {
      toast.push(
        kind === "approve" ? "Approved" : kind === "reject" ? "Rejected" : "Marked paid",
        kind === "reject" ? "warn" : "success",
      );
      qc.invalidateQueries({ queryKey: ["withdrawals"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e)),
  });

  const titles = {
    approve: "Approve withdrawal",
    reject: "Reject withdrawal",
    "mark-paid": "Mark paid",
  };

  return (
    <Modal open onClose={onClose} title={titles[kind]}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          submit.mutate();
        }}
        className="space-y-3"
      >
        <div className="rounded-sm border border-term-border bg-term-elev px-2 py-1.5 text-xs text-term-dim">
          <div>
            <span className="text-term-mute">Amount</span>{" "}
            <span className="font-mono text-term-fg">{row.amount} {row.currency}</span>
          </div>
          <div>
            <span className="text-term-mute">Account</span>{" "}
            <span className="font-mono">{row.trading_account_id.slice(0, 8)}</span>
          </div>
          {row.note && (
            <div>
              <span className="text-term-mute">Client note</span> {row.note}
            </div>
          )}
        </div>
        <div>
          <Label>Decision note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional" />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            loading={submit.isPending}
            variant={kind === "reject" ? "danger" : "primary"}
          >
            {kind === "reject" ? "Reject" : kind === "mark-paid" ? "Mark paid" : "Approve"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
